import { Component, Input, Output, EventEmitter, inject, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { Entity } from '../../../../core/data-models/Entity';
import { LanguageService } from '../../../../core/services/language.service';
import { ModuleName } from '../../../../shared/enums/ModuleName';
import { ThemeModule } from '../../../../shared/Theme/theme.module';
import { ChildrenAnimation, inOutAnimation, slidesAniamtion } from '../../../../shared/animations/allAnimations';

export interface CompaniesTableOptions {
    data: Entity[];
    columns: { field: string; title: string }[];
}

export interface CompanySizeOption {
    key: string;
    name: string;
}

/**
 * Companies Settings Component
 * 
 * Handles company management for Super Admin including:
 * - Display companies in a table
 * - Add new company
 * - Edit existing company
 */
@Component({
    selector: 'companies-settings',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ThemeModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    templateUrl: './companies-settings.component.html',
    styleUrls: ['./companies-settings.component.scss'],
    animations: [inOutAnimation, slidesAniamtion, ChildrenAnimation]
})
export class CompaniesSettingsComponent {
    private languageService = inject(LanguageService);

    readonly ModuleNames = ModuleName;

    @Input() companies: Entity[] = [];
    @Input() language: string = 'EN';
    @Input() companiesOptions!: CompaniesTableOptions;
    @Input() companySizesList: CompanySizeOption[] = [];

    @Output() addCompany = new EventEmitter<void>();
    @Output() editCompany = new EventEmitter<{ index: number; company: Entity }>();

    /**
     * Extract name in current language from translation array
     */
    extractNameLanguage(name: any): string {
        return this.languageService.extractNameLanguage(name);
    }

    /**
     * Return human-readable company size label
     */
    returnCompanySize(size: string): string {
        return this.companySizesList?.find(key => key.key === size)?.name || size;
    }

    onAddCompany(): void {
        this.addCompany.emit();
    }

    onEditCompany(index: number, company: Entity): void {
        this.editCompany.emit({ index, company });
    }
}
